import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalT } from '../../i18n/usePortalT';

const OPEN_ENDED = '2099-12-31T23:59:59.000Z';

export function emptyCampaignForm() {
  const now = new Date();
  return {
    slug: '',
    titleZh: '',
    titleEn: '',
    descriptionZh: '',
    descriptionEn: '',
    status: 'draft',
    startsAt: now.toISOString(),
    endsAt: OPEN_ENDED,
  };
}

export function campaignToForm(campaign) {
  return {
    slug: campaign.slug,
    titleZh: campaign.titleZh,
    titleEn: campaign.titleEn,
    descriptionZh: campaign.descriptionZh || '',
    descriptionEn: campaign.descriptionEn || '',
    status: ['active', 'ended'].includes(campaign.status) ? campaign.status : 'draft',
    startsAt: campaign.startsAt || new Date().toISOString(),
    endsAt: campaign.endsAt || OPEN_ENDED,
  };
}

export { OPEN_ENDED };

export default function CampaignForm({ form, onChange, isEdit = false }) {
  const { t } = usePortalT();
  const set = (key, value) => onChange({ ...form, [key]: value });

  return (
    <div>
      {!isEdit ? (
        <label style={fieldStyle}>
          slug
          <input
            value={form.slug}
            onChange={(e) => set('slug', e.target.value)}
            placeholder="chameleon-2026"
            autoCapitalize="off"
            style={inputStyle}
          />
        </label>
      ) : (
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>slug: <strong>{form.slug}</strong></p>
      )}
      <label style={fieldStyle}>
        {t('campaigns.titleZh')}
        <input value={form.titleZh} onChange={(e) => set('titleZh', e.target.value)} style={inputStyle} />
      </label>
      <label style={fieldStyle}>
        {t('campaigns.titleEn')}
        <input value={form.titleEn} onChange={(e) => set('titleEn', e.target.value)} style={inputStyle} />
      </label>
      <label style={fieldStyle}>
        {t('campaigns.descZh')}
        <textarea value={form.descriptionZh} onChange={(e) => set('descriptionZh', e.target.value)} style={{ ...inputStyle, minHeight: 88 }} />
      </label>
      <label style={fieldStyle}>
        {t('campaigns.descEn')}
        <textarea value={form.descriptionEn} onChange={(e) => set('descriptionEn', e.target.value)} style={{ ...inputStyle, minHeight: 88 }} />
      </label>
      <label style={fieldStyle}>
        {t('common.status')}
        <select value={form.status} onChange={(e) => set('status', e.target.value)} style={inputStyle}>
          <option value="draft">{t('campaigns.statusDraft')}</option>
          <option value="active">{t('campaigns.statusActive')}</option>
          <option value="ended">{t('campaigns.statusEnded')}</option>
        </select>
      </label>
      <label style={fieldStyle}>
        {t('campaigns.startsAt')}
        <input value={form.startsAt} onChange={(e) => set('startsAt', e.target.value)} style={inputStyle} />
      </label>
      <label style={fieldStyle}>
        {t('campaigns.endsAt')}
        <input value={form.endsAt} onChange={(e) => set('endsAt', e.target.value)} style={inputStyle} />
      </label>
      <button type="button" onClick={() => set('endsAt', OPEN_ENDED)} style={linkBtnStyle}>
        {t('campaigns.setOpenEnded')}
      </button>
    </div>
  );
}

const fieldStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginBottom: 14,
  fontSize: 11,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#64748b',
};

const inputStyle = {
  fontSize: 14,
  fontWeight: 500,
  textTransform: 'none',
  letterSpacing: 'normal',
  color: '#2C2A29',
  border: '1px solid #ececef',
  borderRadius: 12,
  padding: '10px 12px',
  background: '#f6f4f0',
  fontFamily: 'inherit',
};

const linkBtnStyle = {
  background: 'none',
  border: '1px solid #ececef',
  borderRadius: 10,
  padding: '8px 12px',
  color: '#0F766E',
  fontWeight: 700,
  fontSize: 12,
  cursor: 'pointer',
};

export function useCampaignFormSubmit({ form, campaignId, isEdit, onSuccess }) {
  const navigate = useNavigate();
  const { t } = usePortalT();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    const slug = form.slug.trim().toLowerCase();
    if ((!isEdit && !slug) || !form.titleZh.trim() || !form.titleEn.trim()) {
      setError(t('campaigns.formRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { createAdminPvpCampaign, updateAdminPvpCampaign, DEFAULT_ROOM_PRESET } = await import('../../lib/pvpCampaignApi');
      if (isEdit) {
        const out = await updateAdminPvpCampaign(campaignId, {
          titleZh: form.titleZh.trim(),
          titleEn: form.titleEn.trim(),
          descriptionZh: form.descriptionZh.trim(),
          descriptionEn: form.descriptionEn.trim(),
          status: form.status,
          startsAt: form.startsAt,
          endsAt: form.endsAt,
        });
        if (!out.success) {
          setError(out.error || t('common.saveFailed'));
          return;
        }
        if (onSuccess) onSuccess();
        else navigate(`/admin/campaigns/${campaignId}`);
      } else {
        const out = await createAdminPvpCampaign({
          slug,
          titleZh: form.titleZh.trim(),
          titleEn: form.titleEn.trim(),
          descriptionZh: form.descriptionZh.trim(),
          descriptionEn: form.descriptionEn.trim(),
          status: form.status,
          startsAt: form.startsAt,
          endsAt: form.endsAt,
          roomPreset: DEFAULT_ROOM_PRESET,
        });
        if (!out.success) {
          setError(out.error || t('common.createFailed'));
          return;
        }
        if (onSuccess) onSuccess();
        else navigate(`/admin/campaigns/${out.id}`);
      }
    } finally {
      setSaving(false);
    }
  };

  return { submit, saving, error, setError };
}
